import { useId } from 'react';

import type { ShoppingList } from './itemsApi.ts';

interface ListSwitcherProps {
  lists: ShoppingList[];
  selected: ShoppingList;
  onSelect: (listId: string) => void;
}

/**
 * Switches between the group's lists.
 *
 * A native <select>, for the same reasons as the group switcher: it is operable
 * one-handed, gets the platform's own picker on a phone, and is accessible for free.
 *
 * It switches and nothing else — creating a list lives in Group settings. Putting an
 * action inside a select is a well-known trap: it fires on arrow-key navigation for
 * keyboard users, who would create a list simply by scrolling past the option.
 *
 * Hidden when there is only one list, which is the common case. A picker with one option
 * is clutter on a screen that has to stay calm.
 */
export function ListSwitcher({ lists, selected, onSelect }: ListSwitcherProps) {
  const id = useId();

  if (lists.length < 2) {
    return null;
  }

  return (
    <div className="list-switcher">
      <label className="visually-hidden" htmlFor={id}>
        List
      </label>
      <select
        id={id}
        className="list-switcher__select"
        value={selected.id}
        onChange={(event) => onSelect(event.target.value)}
      >
        {lists.map((list) => (
          <option key={list.id} value={list.id}>
            {/* The count is why the API returns outstandingCount: it lets someone see
                there are six things still to get on the other list without opening it. */}
            {list.outstandingCount > 0 ? `${list.name} (${list.outstandingCount})` : list.name}
          </option>
        ))}
      </select>
    </div>
  );
}
